import React, { useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef } from "material-react-table"
import { Box, MenuItem, Stack } from "@mui/material"

import { Receipt, ReceiptDialog } from "../../../../../receipt/pages/ReceiptDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { TableNumberOfItemsBox, TableSummaryWrap } from "../../../../../../shared/components/table/TableSummaryBox"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { COLUMN_SIZE } from "../../../../../../shared/constants/table/config"
import { Order } from "../../../../../merchant/controlKitchenTypes"
import { DatePeriod } from "../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { useExportPurchasesToCsv } from "../hooks/useExportPurchases"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { SimpleStatusChip } from "../../../../../merchant/merchant/production/meetingService/components/StatusMenu"

import {
    fetchPurchasesByPeriod,
    FetchPurchasesResponse,
    PurchaseOrderTypeFilter,
    UpcomingOrdersPurchases,
} from "../purchasesApi"
import { TimeOrDatePicker } from "../../../../../../shared/components/table/TimeOrDatePicker"

const useStyles = makeStyles()(() => ({
    table: {
        ".MuiTableCell-root": {
            paddingLeft: "1rem",
            paddingRight: "1rem",
        },
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    tableContainer: {
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
    },
}))

function transferData(data: FetchPurchasesResponse) {
    return data.orders ?? []
}

type UserPurchasesProps = Readonly<{
    orderType?: PurchaseOrderTypeFilter
    initialPeriod?: DatePeriod
}>

export function UserPurchases({ orderType, initialPeriod }: UserPurchasesProps) {
    const { classes } = useStyles()
    const [receipt, setReceipt] = useState<Receipt>()
    const [datePeriod, setDatePeriod] = useState<DatePeriod>(initialPeriod ?? DatePeriod.ALL)

    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState({
        startDate: startDate,
        endDate: endDate,
    })

    const { l10n } = useAppLocalization()

    const purchasesList = useApiQuery(fetchPurchasesByPeriod, {
        queryName: UpcomingOrdersPurchases.UPCOMING_ORDERS_PURCHASES,
        dependencies: {
            period: isDatePicker ? undefined : datePeriod,
            startDate: isDatePicker ? dateState.startDate.toISODate() : undefined,
            endDate: isDatePicker ? dateState.endDate.toISODate() : undefined,
            orderType,
        },
        select: transferData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(purchasesList)

    const columns = useMemo<MRT_ColumnDef<Order>[]>(() => {
        const isTakeaway = orderType === PurchaseOrderTypeFilter.TAKEAWAY
        return [
            columnFactory.idColumn({
                header: l10n.getString("production-order-number"),
                accessorKey: "id",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("production-order-delivery-time"),
                    minSize: COLUMN_SIZE.SM,
                },
                (row) => row.deliveryDetails?.time ?? row.deliveries?.[0]?.deliveryTime,
            ),
            ...(isTakeaway
                ? [
                      columnFactory.statusColumn({
                          header: l10n.getString("upcoming-orders-purchases-table-delivery-status"),
                          Cell: ({ row }) => {
                              const statusDetails = row.original.deliveryDetails?.statusDetails
                              if (!statusDetails?.status) return null
                              return (
                                  <Stack alignItems="center">
                                      <SimpleStatusChip
                                          status={statusDetails.status}
                                          tooltipLabel={statusDetails.message}
                                      />
                                  </Stack>
                              )
                          },
                      }),
                  ]
                : []),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("office-purchase-amount"),
                    id: "amount",
                },
                (row) => row.amount,
                true,
            ),
        ]
    }, [l10n, columnFactory, orderType])

    function showReceipt(row: Order) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const handleChangeTimeOrDatePicker = () => {
        setIsDatePicker((prev) => !prev)
    }

    const { exportPurchasesToCsv } = useExportPurchasesToCsv()

    return (
        <>
            <Box className={classes.table}>
                <ApiQueryTable
                    columns={columns}
                    layoutMode="grid"
                    managePaginationViaUrl={false}
                    scrollToTopOnPageChange
                    initialState={{ density: "spacious" }}
                    muiTableContainerProps={{ className: classes.tableContainer }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="office-purchase-receipt">
                            <MenuItem
                                onClick={() => {
                                    showReceipt(row.original)
                                    closeMenu()
                                }}
                                key={`receipt-${row.original.id}`}
                            >
                                Receipt
                            </MenuItem>
                        </LocalizedStrict>,
                    ]}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table}>
                            <StandardExportButton
                                onClick={() => {
                                    exportPurchasesToCsv(purchasesList.data)
                                }}
                            />
                        </StandardTableFilter>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <TimeOrDatePicker
                                isDatePicker={isDatePicker}
                                dateState={dateState}
                                setDateState={setDateState}
                                timePeriod={datePeriod}
                                handleChangeTimePeriod={setDatePeriod}
                                handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                                timeLabelTranslationKey="production-order-delivery-time"
                                timeLabel="Delivery time"
                            />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap>
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={purchasesList.isLoading}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>
                        </TableSummaryWrap>
                    )}
                />
            </Box>

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={purchasesList.data}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
